import { useCallback, useState } from 'react';
import { Button } from '@ds';
import { useResourcesHandlers } from '@lp/features/RoutePlanner/hooks/useResourcesHandlers';
import { useHubResourcesList } from '@lp/features/RoutePlanner/hooks/useHubResourcesList';
import { useResource } from '@lp/features/RoutePlanner/hooks/useResource';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { ZonedDateTime } from '@internationalized/date';
import { useGoTimeState } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/hooks';
import { CSAssignmentCandidate } from '@/api/apis/resources';
import { Case, CaseResourceType } from '@/api/apis/case';
import { RoutePlannerClinicalSpecialistList } from '../ClinicalSpecialistListTable/ClinicalSpecialistList';
import { getTimeZonedDate } from '@/utils/helpers';

type SelectedSecondaryCSProps = {
  onClose: () => void;
  setParentLoading?: (loading: boolean) => void;
};

export const SelectSecondaryCS = ({ onClose, setParentLoading }: SelectedSecondaryCSProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { caseId, organType, donorHospitalDate, donorHospitalTime, donorHospitalTimeZone } =
    useCaseDetailsWithReviews() as Case;
  const orCaseDateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  const { applyGoTime } = useGoTimeState(orCaseDateTime);
  const { handleResourceAssignment } = useResourcesHandlers({ id: caseId });
  const { handleCSConfirmation } = useResource({ type: CaseResourceType.ClinicalSpecialist, id: '', applyGoTime });
  const [selectedSecondaryCS, setSelectedSecondaryCS] = useState<CSAssignmentCandidate | null>(null);
  const [selectedGoTime, setSelectedGoTime] = useState<ZonedDateTime | undefined>();
  const { selectedHub } = useHubResourcesList({ fetchResources: false });

  const handleReserve = useCallback(
    async (secondaryCS: CSAssignmentCandidate) => {
      try {
        setIsLoading(true);
        setParentLoading?.(true);
        await handleResourceAssignment({
          data: {
            clinicalSpecialistIds: [secondaryCS.employeeId],
          },
          type: 'reserve',
        });
        onClose();
      } finally {
        setIsLoading(false);
        setParentLoading?.(false);
      }
    },
    [handleResourceAssignment, onClose, setParentLoading],
  );

  const handleCSSelect = (employee: CSAssignmentCandidate, _cb: () => void, goDateTime: ZonedDateTime) => {
    setSelectedSecondaryCS(employee);
    setSelectedGoTime(goDateTime);
  };

  const handleAddResource = () => {
    if (selectedSecondaryCS) {
      handleCSConfirmation(selectedSecondaryCS, () => handleReserve(selectedSecondaryCS), selectedGoTime);
    }
  };

  return (
    <div className="flex flex-col place-items-stretch gap-6">
      <div className="flex flex-col">
        <div className="px-1 py-2 flex flex-col">
          <RoutePlannerClinicalSpecialistList
            organType={organType}
            selectedHubId={selectedHub?.id}
            selectedResourceId={selectedSecondaryCS?.employeeId}
            onAction={handleCSSelect}
          />
        </div>
      </div>
      <div className="gap-4 flex flex-row justify-end">
        <Button onClick={onClose} variant="outline">
          Discard
        </Button>
        <Button isDisabled={isLoading} variant="solid" color="primary" onPress={handleAddResource}>
          Add Resource
        </Button>
      </div>
    </div>
  );
};
